import { useTranslation } from "react-i18next"

import { FileText, Mail, MessageSquare } from "@/components/icons"
import {
  PERSONAL_CITATION_LABELS,
  type PersonalCitationSourceType,
  personalSourceForCitationType,
} from "@/features/ai-explorer/utils/personal-search-utils"
import { cn } from "@/lib/utils"

const SOURCE_ICONS = { onedrive: FileText, mail: Mail, chats: MessageSquare } as const

type PersonalCitationItemProps = {
  index: number
  title: string
  sourceType: PersonalCitationSourceType
  onNavigateToInline: (index: number) => void
  isHighlighted?: boolean
  messageIndex?: number
}

/**
 * A citation to one of the user's own items (mail, OneDrive, chat), labelled with where it came
 * from. It is deliberately not a link: FILTH serves a personal item only inside the turn that
 * retrieved it, so there is no preview or download to open later, and nothing here leaves the app.
 */
export function PersonalCitationItem({
  index,
  title,
  sourceType,
  onNavigateToInline,
  isHighlighted = false,
  messageIndex,
}: PersonalCitationItemProps) {
  const { t } = useTranslation()
  const label = PERSONAL_CITATION_LABELS[sourceType]
  const source = personalSourceForCitationType(sourceType)
  const Icon = SOURCE_ICONS[source]

  return (
    <div
      data-citation-index={index}
      data-message-index={messageIndex}
      data-source-type={sourceType}
      data-testid="personal-citation-item"
      role="button"
      tabIndex={0}
      className={cn(
        "border-border/50 bg-muted/30 hover:border-primary/30 flex cursor-pointer items-center gap-2 rounded-md border p-2 text-sm transition-colors",
        isHighlighted && "animate-[highlight_2s_ease-in-out]"
      )}
      onClick={() => {
        onNavigateToInline(index)
      }}
      onKeyDown={(event) => {
        if (event.key === "Enter" || event.key === " ") {
          event.preventDefault()
          onNavigateToInline(index)
        }
      }}
    >
      <span className="text-muted-foreground ml-1 shrink-0 text-xs font-medium">{index + 1}.</span>
      <div className="bg-muted flex size-5 shrink-0 items-center justify-center rounded-full">
        <Icon className="text-muted-foreground size-3" />
      </div>
      <span
        className="text-foreground line-clamp-1 min-w-0 flex-1 text-xs font-medium"
        title={title}
      >
        {title}
      </span>
      <span
        className="bg-muted-foreground/10 text-muted-foreground shrink-0 rounded px-1.5 py-0.5 text-[10px] font-medium"
        data-testid="personal-citation-label"
      >
        {t(label.key, { defaultValue: label.defaultValue })}
      </span>
    </div>
  )
}
